import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, readJson } from "../../lib/api";
import { useToast } from "../Toast";
import type { LiveSession } from "../../lib/types";
import { Avatar } from "../Avatar";

function fmtDuration(ms: number): string {
  const s = Math.floor((ms || 0) / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m ${s % 60}s`;
}

/**
 * Manager-only live-sessions panel — ported from legacy dashboard.html's
 * live-list/pollLive(). Polls GET /api/live every 5s per the brief; the
 * per-second tick between polls is local (nowTick), same trick legacy used
 * for its .live-elapsed spans.
 */
export function LiveNowPanel() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [nowTick, setNowTick] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNowTick(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const { data } = useQuery({
    queryKey: ["live-all"],
    queryFn: () => api.get("/api/live").then((r) => readJson<{ live: LiveSession[]; now_ms: number }>(r)),
    refetchInterval: 5000,
  });
  const rows = data?.live || [];

  async function cancel(id: number, name: string) {
    if (!confirm(`Cancel ${name}'s live session? Their timer stops and the run is not saved.`)) return;
    const res = await api.postJson(`/api/live/${id}/cancel`, {});
    if (res.ok) {
      toast("Session cancelled", "ok");
      queryClient.invalidateQueries({ queryKey: ["live-all"] });
    } else {
      toast("Cancel failed", "err");
    }
  }

  return (
    <div className="panel live-panel">
      <h2>Live now <span className="pill live-pill" data-testid="live-count">{rows.length}</span></h2>
      {rows.length === 0 ? (
        <div className="small muted">Nobody trimming right now.</div>
      ) : (
        rows.map((l) => {
          const activeMs = Math.max(0, nowTick - l.started_at - (l.paused_ms || 0));
          return (
            <div key={l.id} className={`live-card${l.paused ? " paused" : ""}`} data-testid="live-card">
              <div className="live-dot" />
              <Avatar playerId={l.player_id} name={l.player_name || "?"} avatarAt={l.player_avatar_at} size={32} />
              <div className="live-main">
                <div className="name">
                  {l.player_name} <span className="muted">· {l.strain_name || "no strain"} · {l.mode}</span>
                </div>
                <div className="meta">
                  {fmtDuration(activeMs)}
                  {l.paused ? " · paused" : ""}
                  {l.stop_pending_at
                    ? ` · stopped — ${Math.max(0, Math.ceil((l.resume_left_ms || 0) / 60000))}m to resume`
                    : ""}
                </div>
              </div>
              <button
                type="button" className="btn btn-ghost btn-sm" data-testid={`live-cancel-${l.id}`}
                onClick={() => cancel(l.id, l.player_name || "trimmer")}
              >
                Cancel
              </button>
            </div>
          );
        })
      )}
    </div>
  );
}
